import moment from 'moment'
import { TimeOff } from '../types/TimeOff'

export type TimeoffCalculatorOptions = {
  rangeStart?: Date | null
  rangeEnd?: Date | null
}

const getTimeoffDateRange = (timeoff: TimeOff) => {
  const startDate = moment(timeoff.startDate).startOf('day')
  const endDate = moment(timeoff.endDate).startOf('day')

  if (!startDate.isValid() || !endDate.isValid()) return null
  if (endDate.isBefore(startDate, 'day')) return null

  return { startDate, endDate }
}

export const timeOffOverlapsReportRange = (
  timeoff: TimeOff,
  rangeStart: Date,
  rangeEnd: Date,
) => {
  const range = getTimeoffDateRange(timeoff)
  if (!range) return false

  const reportStart = moment(rangeStart).startOf('day')
  const reportEnd = moment(rangeEnd).startOf('day')

  return (
    range.startDate.isSameOrBefore(reportEnd, 'day') &&
    range.endDate.isSameOrAfter(reportStart, 'day')
  )
}

export const clipTimeoffToReportRange = (
  timeoff: TimeOff,
  rangeStart: Date,
  rangeEnd: Date,
) => {
  const reportStart = moment(rangeStart).startOf('day')
  const reportEnd = moment(rangeEnd).startOf('day')
  const range = getTimeoffDateRange(timeoff)
  if (!range) return null

  let { startDate, endDate } = range

  if (
    endDate.isBefore(reportStart, 'day') ||
    startDate.isAfter(reportEnd, 'day')
  ) {
    return null
  }

  startDate = moment.max(startDate, reportStart)
  endDate = moment.min(endDate, reportEnd)

  return { startDate, endDate }
}

export const useTimeoffCalculator = () => {
  const countWeekendDays = (
    startDate: moment.Moment,
    endDate: moment.Moment,
  ) => {
    let count = 0
    const currentDate = startDate.clone()

    while (currentDate.isSameOrBefore(endDate, 'day')) {
      if (currentDate.day() === 0 || currentDate.day() === 6) {
        count++
      }
      currentDate.add(1, 'day')
    }

    return count
  }

  const clipToRange = (
    timeoff: TimeOff,
    rangeStart: moment.Moment,
    rangeEnd: moment.Moment,
  ) => {
    const range = getTimeoffDateRange(timeoff)
    if (!range) return null

    let { startDate, endDate } = range

    if (
      endDate.isBefore(rangeStart, 'day') ||
      startDate.isAfter(rangeEnd, 'day')
    ) {
      return null
    }

    startDate = moment.max(startDate, rangeStart)
    endDate = moment.min(endDate, rangeEnd)

    return { startDate, endDate }
  }

  const clipToCurrentYear = (timeoff: TimeOff) => {
    const currentYear = moment().year()
    const yearStart = moment({ year: currentYear, month: 0, date: 1 })
    const yearEnd = moment({ year: currentYear, month: 11, date: 31 })
    return clipToRange(timeoff, yearStart, yearEnd)
  }

  const resolveClip = (options?: TimeoffCalculatorOptions) => {
    if (options?.rangeStart && options?.rangeEnd) {
      const rangeStart = moment(options.rangeStart).startOf('day')
      const rangeEnd = moment(options.rangeEnd).startOf('day')
      return (timeoff: TimeOff) => clipToRange(timeoff, rangeStart, rangeEnd)
    }
    return clipToCurrentYear
  }

  const getBusinessDaysBetween = (
    startDate: moment.Moment,
    endDate: moment.Moment,
  ) => {
    const weekends = countWeekendDays(startDate, endDate)
    const duration = endDate.diff(startDate, 'days') + 1
    return Math.max(duration - weekends, 0)
  }

  const getTotalDaysUsed = (
    allTimesOff: TimeOff[],
    options?: TimeoffCalculatorOptions,
  ) => {
    const clip = resolveClip(options)
    const today = moment().startOf('day')
    const usageCap =
      options?.rangeEnd != null
        ? moment.min(today, moment(options.rangeEnd).startOf('day'))
        : today

    return allTimesOff.reduce((total, timeoff) => {
      const clipped = clip(timeoff)
      if (!clipped) return total

      const { startDate } = clipped
      if (startDate.isAfter(usageCap, 'day')) return total

      let { endDate } = clipped
      endDate = moment.min(endDate, usageCap)

      return total + getBusinessDaysBetween(startDate, endDate)
    }, 0)
  }

  const getTotalDaysScheduled = (
    allTimesOff: TimeOff[],
    options?: TimeoffCalculatorOptions,
  ) => {
    const clip = resolveClip(options)

    return allTimesOff.reduce((total, timeoff) => {
      const clipped = clip(timeoff)
      if (!clipped) return total

      const { startDate, endDate } = clipped
      return total + getBusinessDaysBetween(startDate, endDate)
    }, 0)
  }

  const getDaysPlanned = (
    allTimesOff: TimeOff[],
    options?: TimeoffCalculatorOptions,
  ) => {
    const totalDaysUsed = getTotalDaysUsed(allTimesOff, options)
    const totalDaysScheduled = getTotalDaysScheduled(allTimesOff, options)
    return Math.max(totalDaysScheduled - totalDaysUsed, 0)
  }

  const getRemainingDays = (
    allTimesOff: TimeOff[],
    options?: TimeoffCalculatorOptions,
  ) => getDaysPlanned(allTimesOff, options)

  const getBusinessDaysForTimeoff = (
    timeoff: TimeOff,
    isListView = false,
    options?: TimeoffCalculatorOptions,
  ) => {
    const range = isListView
      ? getTimeoffDateRange(timeoff)
      : resolveClip(options)(timeoff)
    if (!range) return 0

    const { startDate, endDate } = range
    return getBusinessDaysBetween(startDate, endDate)
  }

  return {
    getTotalDaysScheduled,
    getTotalDaysUsed,
    getDaysPlanned,
    getRemainingDays,
    getBusinessDaysForTimeoff,
  }
}
